import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Buildings, ArrowCounterClockwise, MapPin, ArrowLeft, Broadcast } from "@phosphor-icons/react";
import { companyClassMeta } from "@/lib/format";

export default function HiddenCompanies() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hidden-companies");
      setItems(data.hidden || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (companyId) => {
    try {
      await api.delete(`/companies/${companyId}/hide`);
      setItems((prev) => prev.filter((i) => i.company_id !== companyId));
      toast.success("Entreprise restaurée — ses signaux réapparaîtront dans vos recherches");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="hidden-companies-page">
      <button onClick={() => navigate("/app")} data-testid="hidden-companies-back-btn"
        className="flex items-center gap-1.5 text-sm text-muted hover:text-fg mb-4">
        <ArrowLeft size={15} /> Retour à la recherche
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Buildings size={22} className="text-accent" weight="duotone" />
        <h1 className="font-display text-2xl font-bold">Entreprises masquées</h1>
        <span className="text-xs font-mono text-muted bg-surface border border-border rounded-full px-2 py-0.5" data-testid="hidden-companies-count">{items.length}</span>
      </div>
      <p className="text-muted text-sm mb-6">Les entreprises masquées n'apparaissent plus dans les recherches de votre organisation. Aucune donnée n'est supprimée — cette liste d'exclusion est propre à votre organisation et réversible à tout moment.</p>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4" data-testid="hidden-companies-loading">
          {[...Array(4)].map((_, i) => <div key={i} className="surface rounded-lg p-5 h-24 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="surface rounded-lg p-10 text-center text-muted" data-testid="hidden-companies-empty">
          Aucune entreprise masquée. Utilisez « Masquer cette entreprise » sur une carte d'opportunité ou une fiche entreprise pour l'écarter de vos résultats.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="hidden-companies-list">
          {items.map((it) => {
            const ccm = companyClassMeta(it.company_class);
            return (
              <div key={it.company_id} className="surface rounded-lg p-4 flex flex-col gap-2" data-testid={`hidden-company-${it.company_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-fg truncate flex items-center gap-1.5"><Buildings size={15} /> {it.name || "—"}</div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted font-mono mt-1">
                      {it.city && <span className="flex items-center gap-1"><MapPin size={12} /> {it.city}</span>}
                      <span className="flex items-center gap-1"><Broadcast size={12} /> {it.signals_count} signal{it.signals_count > 1 ? "s" : ""}</span>
                      <span className="rounded px-1.5 py-0.5 border" style={{ color: ccm.color, borderColor: ccm.color + "66", background: ccm.color + "14" }}>{ccm.label}</span>
                    </div>
                  </div>
                  <button onClick={() => restore(it.company_id)} data-testid={`restore-company-${it.company_id}`}
                    className="shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-accent/15 text-accent border border-accent/30 px-3 py-1.5 rounded-md hover-lift">
                    <ArrowCounterClockwise size={14} /> Restaurer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
